import React from 'react';
import {
    Settings,
    Package,
    Users,
    MapPin,
    ShieldAlert,
    ChevronRight,
    Truck,
    Tag
} from 'lucide-react';
import { Button } from '../../components/ui/button';

import ManageItems from './ManageItems';
import FieldWorkers from './FieldWorkers';
import Locations from './Locations';
import IssuanceLimits from './IssuanceLimits';
import Suppliers from './Suppliers';
import ItemCategories from './ItemCategories';

interface InventorySettingsProps {
    onNavigate: (view: string) => void;
}

const InventorySettings: React.FC<InventorySettingsProps> = ({ onNavigate }) => {
    const [view, setView] = React.useState<'main' | 'items' | 'workers' | 'locations' | 'limits' | 'suppliers' | 'categories'>('main');

    const settingsOptions = [
        {
            id: 'items',
            label: 'Manage Items',
            icon: <Package className="text-gray-400" size={20} />,
            description: 'Categories, units, and item properties'
        },
        {
            id: 'categories',
            label: 'Item Category',
            icon: <Tag className="text-gray-400" size={20} />,
            description: 'Define and organize item groupings'
        },
        {
            id: 'suppliers',
            label: 'Suppliers',
            icon: <Truck className="text-gray-400" size={20} />,
            description: 'Vendor management and contact details'
        },
        {
            id: 'workers',
            label: 'Field Workers',
            icon: <Users className="text-gray-400" size={20} />,
            description: 'Worker profiles and assignments'
        },
        {
            id: 'locations',
            label: 'Locations',
            icon: <MapPin className="text-gray-400" size={20} />,
            description: 'Warehouse and site management'
        },
        {
            id: 'limits',
            label: 'Issuance Limits',
            icon: <ShieldAlert className="text-gray-400" size={20} />,
            description: 'Quota and restriction policies'
        }
    ];

    if (view === 'items') {
        return <ManageItems onBack={() => setView('main')} />;
    }

    if (view === 'categories') {
        return <ItemCategories onBack={() => setView('main')} />;
    }

    if (view === 'suppliers') {
        return <Suppliers onBack={() => setView('main')} />;
    }

    if (view === 'workers') {
        return <FieldWorkers onBack={() => setView('main')} />;
    }

    if (view === 'locations') {
        return <Locations onBack={() => setView('main')} />;
    }

    if (view === 'limits') {
        return <IssuanceLimits onBack={() => setView('main')} />;
    }

    return (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-20 flex flex-col items-center justify-center text-center animate-in fade-in zoom-in-95 duration-500">
            <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-6">
                <Settings size={40} className="text-gray-300 animate-spin-slow" />
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-2">Settings</h2>
            <p className="text-gray-500 mb-8 sm:mb-12 max-w-sm">
                Manage items, workers, locations, and issuance limits
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-2xl">
                {settingsOptions.map((option) => (
                    <button
                        key={option.id}
                        onClick={() => setView(option.id as any)}
                        className="flex items-center justify-between p-4 sm:p-6 bg-white border border-gray-100 rounded-2xl hover:border-blue-200 hover:shadow-md hover:shadow-blue-500/5 transition-all group text-left"
                    >
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gray-50 flex items-center justify-center group-hover:bg-blue-50 transition-colors shrink-0">
                                {React.cloneElement(option.icon as React.ReactElement, {
                                    className: "group-hover:text-blue-600 transition-colors w-5 h-5 sm:w-5 sm:h-5"
                                })}
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 text-sm sm:text-base">{option.label}</h3>
                                <p className="text-[10px] sm:text-xs text-gray-400 line-clamp-1">{option.description}</p>
                            </div>
                        </div>
                        <ChevronRight size={18} className="text-gray-200 group-hover:text-blue-400 transition-all group-hover:translate-x-1" />
                    </button>
                ))}
            </div>
        </div>
    );
};

export default InventorySettings;
